import type { WritableSignal } from '@angular/core'
import { Injectable, signal, inject } from '@angular/core'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import { apiEndpoint } from '@awork/environments/environment'
import type { Observable } from 'rxjs'
import { forkJoin, of, ReplaySubject, map, switchMap, tap } from 'rxjs'
import { HttpParams } from '@angular/common/http'
import type { Search } from '@awork/framework/models/search.model'
import { ProjectStore } from '@awork/features/project/state/project.store'
import { ProjectQuery } from '@awork/features/project/state/project.query'
import { TaskStore } from '@awork/features/task/state/task.store'
import { TaskQuery } from '@awork/features/task/state/task.query'
import { ProjectService } from '@awork/features/project/services/project-service/project.service'
import { TaskService } from '@awork/features/task/services/task-service/task.service'
import type { Project } from '@awork/features/project/models/project.model'
import type { Task } from '@awork/features/task/models/task.model'

export enum SearchEntities {
  all = 'all',
  project = 'project',
  task = 'task',
  user = 'user',
  client = 'company',
  document = 'document',
  thread = 'thread'
}

export interface SearchOptions {
  includeClosedAndStuck?: boolean
}

@Injectable({ providedIn: 'root' })
export class SearchService {
  private apiClient = inject(ApiClient)
  private projectStore = inject(ProjectStore)
  private taskStore = inject(TaskStore)
  private projectQuery = inject(ProjectQuery)
  private taskQuery = inject(TaskQuery)
  private projectService = inject(ProjectService)
  private taskService = inject(TaskService)

  private url: string
  savedSearchText: string
  savedSearchEntity: SearchEntities

  includeArchivedEntities: WritableSignal<boolean> = signal(false)

  private resultCache$ = new ReplaySubject<Search>(1)
  private cachedSearchText: string

  constructor() {
    this.url = `${apiEndpoint}/search`
  }

  /**
   * Makes an API call to make a smart search
   * @param {string} searchTerm
   * @param {number} numberOfItems the number of items returned for each area
   * @param {string[]} types optional - the types we want to search for (e.g. taskentity,projectentity)
   * @param {boolean} [syncStore=false] optional - True to make calls to get the whole models and sync the store
   * @param {boolean} [useCache=false] Returns the cache if available
   * @param {SearchOptions} [options] Optional request overrides
   * @returns {Observable<Search>} search result
   */
  getSearch(
    searchTerm: string,
    numberOfItems: number,
    types?: string[],
    syncStore = false,
    useCache = false,
    options?: SearchOptions
  ): Observable<Search> {
    let params = new HttpParams()

    params = params.append('searchTerm', searchTerm)
    params = params.append('top', numberOfItems.toString())
    params = params.append('includeClosedAndStuck', options?.includeClosedAndStuck ?? this.includeArchivedEntities())

    if (types) {
      params = params.append('searchTypes', types.join())
    }

    const apiCall$ = this.apiClient.get<Search>(`${this.url}`, { params }).pipe(
      switchMap(result => {
        if (syncStore) {
          // Users without the PM product get no task hits, even when the request asks for tasks
          const projectHits = result.projectHits?.hits ?? []
          const taskHits = result.taskHits?.hits ?? []
          const projectIds = []
          const taskIds = []
          let projectFilter = ''
          let taskFilter = ''

          // Get project ids and filter string
          projectHits.forEach((hit, index, array) => {
            projectIds.push(hit.entity.id)
            projectFilter += `id eq guid'${hit.entity.id}'`

            if (index < array.length - 1) {
              projectFilter += ' or '
            }
          })

          // Get task ids and filter string
          taskHits.forEach((hit, index, array) => {
            taskIds.push(hit.entity.id)
            taskFilter += `id eq guid'${hit.entity.id}'`

            if (index < array.length - 1) {
              taskFilter += ' or '
            }
          })

          // Fetch projects and tasks full models
          return forkJoin([
            projectIds.length ? this.projectService.fetchProjects({ filterBy: projectFilter }) : of([] as Project[]),
            taskIds.length ? this.taskService.fetchAllTasks({ filterBy: taskFilter }) : of([] as Task[])
          ]).pipe(
            tap(responses => {
              // Map scores into entities
              responses[0] = responses[0].map(project => {
                const foundProject = projectHits.find(hit => hit.entity.id === project.id)
                return {
                  ...project,
                  score: foundProject ? foundProject.score : 1
                }
              }) as Project[]

              responses[1] = responses[1].map(task => {
                const foundTask = taskHits.find(hit => hit.entity.id === task.id)
                return {
                  ...task,
                  score: foundTask ? foundTask.score : 1
                }
              }) as Task[]

              // Sync stores
              const existingProjects = this.projectQuery.queryProjects(projectIds)

              this.projectStore.sync(responses[0], existingProjects())

              const existingTasks = this.taskQuery.queryMany(taskIds)

              this.taskStore.sync(responses[1], existingTasks())
            }),
            map(() => result)
          )
        }

        this.resultCache$.next(result)
        this.cachedSearchText = searchTerm

        return of(result)
      })
    )

    if (useCache && searchTerm === this.cachedSearchText) {
      apiCall$.subscribe()

      return this.resultCache$
    } else {
      return apiCall$
    }
  }
}
